<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2 mb-3"
    >
      <BCol
        cols="12"
        md="6"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="gridEvents.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Activity"
          label-for="filterActivity"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="gridEvents.selected.activity"
            placeholder="filter activity here"
            input-id="filterActivity"
            label="activity_name"
            class="border border-light rounded-lg"
            clearable
            append-to-body
            :options="(gridEvents.options.activities)"
            :loading="(gridEvents.fetching.activities)"
            :disabled="(gridEvents.busy || gridEvents.fetching.activities)"
            :calculate-position="calculateDropPosition"
            @search="searchFilterActivities"
          >
            <template #no-options="">
              no available activity
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Month"
          label-for="filterMonth"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <flat-pickr
            id="filterMonth"
            v-model="gridEvents.filter.month"
            class="form-control"
            placeholder="--------- ----"
            :config="flatPickerConfig"
          />
        </BFormGroup>
      </BCol>
      <BCol
        cols="12"
      >
        <BButton
          size="sm"
          variant="primary"
          @click.prevent="createEvent"
        >
          Record Activity
        </BButton>
      </BCol>
    </BRow>

    <BRow
      v-if="gridEvents.busy"
      class="gap-y-6 mb-3"
    >
      <BCol
        cols="12"
      >
        <div class="text-center py-5">
          <BIcon
            icon="stopwatch"
            font-scale="5"
            animation="cylon"
          />
          <p class="h3 py-2">
            <strong>Loading . . .</strong>
          </p>
        </div>
      </BCol>
    </BRow>

    <BRow
      v-else-if="Number(gridEvents.rows.length) <= 0 && !gridEvents.busy"
    >
      <BCol
        cols="12"
        class="text-center py-5"
      >
        <BIcon
          icon="calendar"
          font-scale="5"
        />
        <p class="h3 py-2">
          <strong>No activity found</strong>
        </p>
      </BCol>
    </BRow>

    <BRow
      v-else
      class="gap-y-6 mb-3"
    >
      <BCol
        v-for="(event, index) in gridEvents.rows"
        :key="index"
        cols="12"
        lg="6"
      >
        <Event
          :details="{
            id: event.id,
            activity_name: event?.activity?.activity_name || '',
            province_name: event?.province?.province_name || '',
            municipality_name: event?.municipality?.municipality_name || '',
            event_date: event.event_date,
            remarks: event.remarks
          }"
          :attachment-path="event.attachment_path || ''"
          :attachments="event.attachments"
          :loading="gridEvents.busy"
          @show:attachment="attachmentIndex => openPictures(event, attachmentIndex)"
          @more:attachments="() => editEvent(event)"
        >
          <template #footer>
            <div class="d-flex justify-content-between">
              <div>
                {{ dateTimeShortFormatter(event.created_at) }}
              </div>
              <div class="d-flex gap-2">
                <BButton
                  size="sm"
                  variant="warning"
                  @click.prevent="() => editEvent(event, true)"
                >
                  View
                </BButton>
                <BButton
                  v-if="isEventEditable(event)"
                  size="sm"
                  variant="primary"
                  @click.prevent="() => editEvent(event, false)"
                >
                  Edit
                </BButton>
                <BButton
                  size="sm"
                  variant="outline-danger"
                  @click.prevent="deleteEvent(event)"
                >
                  Delete
                </BButton>
              </div>
            </div>
          </template>
        </Event>
      </BCol>
    </BRow>

    <BRow>
      <BCol
        cols="12"
        md="6"
        class="mt-1 mb-md-0 mb-2"
      >
        <BSelect
          v-model="gridEvents.perPage"
          :options="gridEvents.pageOptions"
          :disabled="gridEvents.busy"
          class="w-100 w-md-75px"
          size="sm"
        />
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1"
      >
        <BPagination
          v-model="gridEvents.currentPage"
          :total-rows="gridEvents.totalRows"
          :per-page="gridEvents.perPage"
          :disabled="gridEvents.busy"
          class="mb-0"
          aria-controls="table"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          pills
        />
      </BCol>
    </BRow>

    <BModal
      ref="modalOtherEvent"
      size="lg"
      title="Event"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      @shown="eventModalShown"
      @hidden="eventModalHidden"
    >
      <BTabs
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-task-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-file-lines', {
                  'text-danger fat fa-shake': vTabEventDetails()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabEventDetails()
                }]"
              >
                Details
              </b>
            </div>
          </template>

          <ValidationObserver
            ref="formEvent"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>

                <BCol
                  cols="12"
                  md="3"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="event_date"
                    name="date"
                    rules="required"
                  >
                    <BFormGroup
                      label="Event Date"
                      label-for="fEventDate"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fEventDate"
                        v-model="formEvent.event_date"
                        type="date"
                        :min="formEventValidation.eventDate.min"
                        :max="formEventValidation.eventDate.max"
                        :state="errors.length > 0 ? false : null"
                        :disabled="state.busy || state.viewing"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="activity_id"
                    name="activity"
                    rules="required"
                  >
                    <BFormGroup
                      label="Activity"
                      label-for="fActivity"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.activity"
                        input-id="fActivity"
                        label="activity_name"
                        clearable
                        append-to-body
                        placeholder="select activity here"
                        :options="options.activities"
                        :disabled="[
                          fetching.activities,
                          state.busy,
                          state.viewing,
                          formEvent.attachments.length > 0
                        ].some(isTrue => isTrue)"
                        :loading="fetching.activities"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchActivities"
                      >
                        <template #option="{ activity_name }">
                          <div
                            class="text-break text-wrap"
                            v-text="activity_name"
                          />
                        </template>
                        <template #selected-option="{ activity_name }">
                          <div
                            class="w-225px w-md-325px text-truncate"
                            v-text="activity_name"
                          />
                        </template>
                        <template #no-options="">
                          no available activity
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="province_id"
                    name="province"
                    rules="required"
                  >
                    <BFormGroup
                      label="Province"
                      label-for="fProvince"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.province"
                        input-id="fProvince"
                        label="province_name"
                        clearable
                        append-to-body
                        :placeholder="'select province here'"
                        :options="options.provinces"
                        :disabled="[
                          state.busy,
                          fetching.provinces,
                          state.viewing
                        ].some(isTrue => isTrue)"
                        :loading="fetching.provinces"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchProvinces"
                      >
                        <template #option="{ province_name, region }">
                          <div
                            class="text-break text-wrap"
                            v-text="province_name"
                          />
                          <div
                            v-if="region"
                            class="text-muted small"
                            v-text="region?.region_name"
                          />
                        </template>
                        <template #selected-option="{ province_name }">
                          <div
                            class="w-200px w-lg-325px text-truncate"
                            v-text="province_name"
                          />
                        </template>
                        <template #no-options="">
                          no available province
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="municipality_id"
                    name="municipality"
                    rules="required"
                  >
                    <BFormGroup
                      label="Municipality"
                      label-for="fMunicipality"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <VSelect
                        v-model="selected.municipality"
                        input-id="fMunicipality"
                        label="municipality_name"
                        clearable
                        append-to-body
                        :placeholder="'select municipality here'"
                        :options="options.municipalities"
                        :disabled="[
                          state.busy,
                          fetching.municipalities,
                          fetching.provinces,
                          state.viewing
                        ].some(isTrue => isTrue)"
                        :loading="fetching.municipalities"
                        :calculate-position="calculateDropPosition"
                        :class="{ 'is-invalid': errors.length > 0 }"
                        @search="searchMunicipalities"
                      >
                        <template #option="{ municipality_name }">
                          <div
                            class="text-break text-wrap"
                            v-text="municipality_name"
                          />
                        </template>
                        <template #selected-option="{ municipality_name }">
                          <div
                            class="w-200px w-lg-325px text-truncate"
                            v-text="municipality_name"
                          />
                        </template>
                        <template #no-options="">
                          no available municipality
                        </template>
                      </VSelect>
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="remarks"
                    name="remarks"
                    rules="required|max:1000"
                  >
                    <BFormGroup
                      label="Remarks"
                      label-for="fRemarks"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <template #label>
                        <span class="d-flex justify-content-between">
                          Remarks
                          <span>
                            {{ formEvent.remarks ? formEvent.remarks.length : 0 }} / 1000
                          </span>
                        </span>
                      </template>
                      <BFormTextarea
                        id="fRemarks"
                        v-model="formEvent.remarks"
                        placeholder="enter remarks here"
                        rows="7"
                        max-rows="9999"
                        maxlength="1000"
                        :state="errors.length > 0 ? false : null"
                        :disabled="state.busy || state.viewing"
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>
              </BRow>
            </form>
          </ValidationObserver>
        </BTab>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i
                :class="['fa-xl fad fa-paperclip', {
                  'text-danger fat fa-shake': vTabAttachments()
                }]"
              />
              <b
                :class="['is-tab-active ml-1', {
                  'text-danger': vTabAttachments()
                }]"
              >
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            :loading="state.busy"
            :valid-id="formEvent.id"
            :document-types="formEventValidation.documents"
            :path="formEvent.attachment_path"
            :files="formEvent.attachments"
            :deleted-files="formEvent.deleted_files"
            :show-uploader="!state.viewing"
            @files:updated="updateEventAttachments"
          />
        </BTab>
        <BTab>
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-map-marker-alt" />
              <b class="is-tab-active ml-1">
                Location
              </b>
            </div>
          </template>

          <GeoLocationMap
            ref="geoLocationMap"
            :enabled="!state.viewing"
            :coords="formEvent.geolocation"
            @updated:coords="updateEventGeolocation"
          />
        </BTab>
      </BTabs>
      <template #modal-footer="{ cancel }">
        <div class="w-100 d-flex justify-content-end align-items-center gap-2">
          <BButton
            v-if="!state.viewing"
            variant="primary"
            :disabled="state.busy"
            @click.prevent="validateEvent"
          >
            {{ state.editing ? 'Update' : 'Record' }} Activity
          </BButton>
          <BButton
            variant="outline-dark"
            :disabled="state.busy"
            @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
          >
            {{ state.editing && !state.viewing ? 'Discard' : 'Close' }}
          </BButton>
        </div>
      </template>
    </BModal>

    <Preview
      ref="modalPreview"
    />
  </BContainer>
</template>
<script>
import QUERY from 'lodash'
import MOMENT from 'moment'
import { mapGetters } from 'vuex'
import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'

import Event from '@/views/fso/activity-recordings/components/Event.vue'
import Attachment from '@/views/fso/activity-recordings/components/Attachment.vue'
import Preview from '@/views/fso/activity-recordings/components/Preview.vue'
import GeoLocationMap from '@/components/custom/GeoLocationMap.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MProvinces from '@/mixins/locations/provinces'
import MMunicipalities from '@/mixins/locations/municipalities'
import MActivities from '@/mixins/maintenances/activities'

import { SFSOActivityRecording } from '@/services/fso'

export default {
  name: 'FSOOtherEventsBase',

  components: {
    Preview,
    Event,
    Attachment,
    GeoLocationMap
  },

  mixins: [
    MxMisc,
    MxFormatter,
    MxImageUrl,
    MProvinces,
    MMunicipalities,
    MActivities
  ],

  middleware: [
    'auth',
    'fso'
  ],

  data () {
    return {
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      geoLocationId: null,
      state: {
        busy: false,
        editing: false,
        viewing: false
      },
      formEventValidation: {
        eventDate: {
          min: MOMENT().startOf('month').format('YYYY-MM-DD'),
          max: MOMENT().endOf('month').format('YYYY-MM-DD')
        },
        documents: [],
        rules: [],
        created_at: ''
      },
      selected: {
        role: 2,
        activityType: 'Others'
      },
      formEvent: {
        id: '',
        event_date: '',
        activity_id: '',
        activity_name: '',
        province_id: '',
        municipality_id: '',
        remarks: '',
        attachment_path: '',
        attachments: [],
        deleted_files: [],
        active: 1,
        geolocation: {
          latitude: 0,
          longitude: 0
        }
      },
      gridEvents: {
        busy: false,
        fetching: {
          activities: false
        },
        options: {
          activities: []
        },
        selected: {
          role: 2,
          activity: '',
          activityType: 'Others'
        },
        filter: {
          search: '',
          activity: '',
          month: MOMENT().format('YYYY-MM')
        },
        filterOn: [],
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: []
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    })
  },

  watch: {
    'selected.activity' (activity) {
      this.formEventValidation.rules = activity?.rules || []
      this.formEventValidation.documents = activity?.documents || []

      this.formEvent.activity_id = activity?.id || ''
      this.formEvent.activity_name = activity?.activity_name || ''
    },

    'selected.province' (province) {
      this.formEvent.province_id = province?.id || ''
    },

    'selected.municipality' (municipality) {
      this.formEvent.municipality_id = municipality?.id || ''
    },

    'gridEvents.selected.activity' (activity) {
      this.gridEvents.filter.activity = activity?.id || ''
    },

    'gridEvents.filter': {
      handler () {
        this.gridProvider()
      },
      deep: true
    },

    'gridEvents.currentPage' () {
      this.gridProvider()
    },

    'gridEvents.perPage' () {
      this.gridProvider()
    }
  },

  mounted () {
    if (this.currentUser) {
      this.getActivities(this.gridEvents).finally(() => {
        if (this.$route.params.activity) {
          this.gridEvents.selected.activity = QUERY.find(this.gridEvents.options.activities, {
            id: Number(this.$route.params.activity)
          }) || ''
        } else {
          this.gridProvider()
        }
      })
    }
  },

  methods: {

    vTabEventDetails () {
      if (this.$refs.formEvent) {
        const formEventErrors = this.$refs.formEvent.errors
        return Object.keys(formEventErrors).some(
          errorKey => formEventErrors[errorKey]?.length > 0
        )
      }

      return false
    },

    vTabAttachments () {
      if (this.$refs.attachmentUploader) {
        const formDocument = this.$refs.attachmentUploader.$refs.formDocument

        if (formDocument) {
          return Object.keys(formDocument.errors).some(
            errorKey => formDocument.errors[errorKey]?.length > 0
          )
        }
      }

      return false
    },

    isEventEditable (otherEvent) {
      const today = MOMENT()
      const createdAt = MOMENT(otherEvent.created_at)

      if (createdAt.add(72, 'hours').isSameOrBefore(today)) {
        return false
      }

      return true
    },

    async gridProvider () {
      this.gridEvents.busy = true

      const payload = {
        page: this.gridEvents.currentPage,
        per_page: this.gridEvents.perPage,
        sort: this.gridEvents.sortBy,
        sort_desc: this.gridEvents.sortDesc,
        filter_text: this.gridEvents.filter.search,
        activity_id: this.gridEvents.filter.activity,
        month: '',
        year: ''
      }

      if (this.gridEvents.filter.month) {
        const month = MOMENT(this.gridEvents.filter.month).format('MM')
        const year = MOMENT(this.gridEvents.filter.month).format('YYYY')

        payload.month = month
        payload.year = year
      }

      return SFSOActivityRecording.get(payload).then(
        ({ data }) => {
          this.gridEvents.totalRows = data.total_rows
          this.gridEvents.rows = data.items
          return data.items
        }
      ).catch(() => {
        this.gridEvents.totalRows = 0
        this.gridEvents.rows = []
        return []
      }).finally(() => {
        this.gridEvents.busy = false
      })
    },

    async searchFilterActivities (search, loading) {
      this.searchActivitiesProvider(this.gridEvents, search, loading)
    },

    updateEventAttachments (attachment) {
      this.formEvent = Object.assign(this.formEvent, {
        attachment_path: attachment.attachment_path,
        attachments: attachment.attachments,
        deleted_files: attachment.deleted_files
      })
    },

    updateEventGeolocation (geolocation) {
      this.formEvent = Object.assign(this.formEvent, {
        geolocation
      })
    },

    openPictures (activity, index) {
      this.$refs.modalPreview.show(activity.attachments, activity.attachment_path, index)
    },

    createEvent () {
      this.state.editing = false
      this.state.viewing = false

      this.formEventValidation.created_at = ''

      const dateFormat = 'YYYY-MM-DD'

      const maxDay = MOMENT()
      const minDay = MOMENT(MOMENT().format('YYYY-01-01'))

      this.formEventValidation.eventDate = Object.assign(this.formEventValidation.eventDate, {
        min: minDay.startOf('month').format(dateFormat),
        max: maxDay.endOf('month').format(dateFormat)
      })

      this.formEvent.event_date = MOMENT().format(dateFormat)

      this.formEvent = Object.assign(this.formEvent, {
        id: '',
        activity_id: '',
        activity_name: '',
        province_id: '',
        municipality_id: '',
        remarks: '',
        attachment_path: '',
        attachments: [],
        deleted_files: [],
        products: [],
        active: 1,
        geolocation: {
          latitude: 0,
          longitude: 0
        }
      })

      this.getProvinces(this)

      this.getActivities(this)

      this.$refs.modalOtherEvent.show()
    },

    editEvent (otherEvent, viewOnly = false) {
      const today = MOMENT()
      const createdAt = MOMENT(otherEvent.created_at)

      if (createdAt.add(72, 'hours').isSameOrBefore(today) && !viewOnly) {
        return this.swalInvalid('You can no longer edit this event. Editing is only allowed within 72 hours from the time it was created.')
      }

      this.state.editing = true
      this.state.viewing = viewOnly

      this.formEventValidation.created_at = otherEvent.created_at

      this.editable.municipality = otherEvent.municipality_id || ''

      this.formEvent = Object.assign(this.formEvent, {
        id: otherEvent.id,
        event_date: MOMENT(otherEvent.event_date).format('YYYY-MM-DD'),
        remarks: otherEvent.remarks,
        attachment_path: otherEvent.attachment_path,
        attachments: otherEvent.attachments || [],
        deleted_files: [],
        products: otherEvent?.purchases || [],
        geolocation: {
          latitude: otherEvent?.geolocation?.latitude || 0,
          longitude: otherEvent?.geolocation?.longitude || 0
        },
        active: otherEvent.active
      })

      this.getProvinces(this).finally(() => {
        this.selected.province = QUERY.find(this.options.provinces, {
          id: otherEvent.province_id
        }) || ''
      })

      this.getActivities(this).finally(() => {
        this.selected.activity = QUERY.find(this.options.activities, {
          id: otherEvent.activity_id
        }) || ''
      })

      this.$refs.modalOtherEvent.show()
    },

    validateEvent () {
      this.$refs.formEvent.validate().then(
        validEvent => {
          if (!validEvent) {
            return this.swalInvalid()
          }

          const today = MOMENT()
          const createdAt = MOMENT(this.formEventValidation.created_at)

          if (createdAt.add(72, 'hours').isSameOrBefore(today) && !this.state.viewing) {
            return this.swalInvalid('You can no longer edit this event. Editing is only allowed within 72 hours from the time it was created.')
          }

          const noAttachments = this.formEvent.attachments.length === 0

          const hasRequiredDocuments = this.formEventValidation.documents.filter(
            documentType => Number(documentType.required) === 1
          ).map(
            documentType => ({
              name: documentType.document_type_name,
              uploaded: this.formEvent.attachments.some(
                attachment => Number(attachment.document_type_id) === Number(documentType.id)
              )
            })
          )

          if (noAttachments || !hasRequiredDocuments.every(doc => doc.uploaded)) {
            const requiredDocuments = hasRequiredDocuments.filter(doc => !doc.uploaded).map(doc => doc.name).join(', ')
            const refAttachment = this.$refs.attachmentUploader
            if (refAttachment) {
              refAttachment.$refs.formDocument.setErrors({
                attachments: `Please upload at least one attachment for ${requiredDocuments}.`
              })
            }

            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Activity?</h6>' : '<h6>Record this Activity?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Record',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putEvent() : this.postEvent()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    if (this.$refs.attachmentUploader) {
                      this.$refs.attachmentUploader.$refs.formDocument.setErrors(message.errors)
                    }

                    this.$refs.formEvent.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postEvent () {
      return SFSOActivityRecording.post(this.formEvent).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalOtherEvent.hide()
            this.gridProvider()
          })
          return data
        }
      )
    },

    async putEvent () {
      return SFSOActivityRecording.put(this.formEvent).then(
        ({ data }) => {
          this.swalSuccess(data.message).then(() => {
            this.$refs.modalOtherEvent.hide()

            this.gridProvider()
          })

          return data
        }
      )
    },

    async deleteEvent (otherTask) {
      return this.swalConfirm({
        html: `<div>
          <h5>Delete this Activity?</h5>
          <b class="text-danger">This action cannot be undone.</b>
        </div>`,
        // remarksRequired: true,
        // remarksMaxLength: 1000,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Keep',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          const payload = {
            id: otherTask.id,
            remarks: ''
          }

          return SFSOActivityRecording.delete(payload).then(
            ({ data }) => {
              this.swalSuccess(data.message).then(() => {
                this.gridProvider()
              })

              return data
            }
          )
        }
      })
    },

    eventModalHidden () {
      this.detachArrowScroller('.nav-task-tabs')
    },

    eventModalShown () {
      this.attachArrowScroller('.nav-task-tabs')

      this.$refs.geoLocationMap.initMap()
    }
  }
}
</script>
